import { getApiEndpoint } from '../config/api.js';

export interface StockAnalysis {
  symbol: string;
  summary: string;
  sentiment: 'bullish' | 'bearish' | 'neutral';
  keyPoints: string[];
  technicalAnalysis: {
    support: number[];
    resistance: number[];
    trend: string;
    indicators: {
      name: string;
      value: string;
      signal: 'buy' | 'sell' | 'hold';
    }[];
  };
  fundamentalAnalysis: {
    strengths: string[];
    weaknesses: string[];
    opportunities: string[];
    threats: string[];
  };
  priceTargets: {
    shortTerm: number;
    mediumTerm: number;
    longTerm: number;
  };
}

export interface NewsAnalysis {
  articles: {
    title: string;
    content: string;
    sentiment: 'positive' | 'negative' | 'neutral';
    impact: 'high' | 'medium' | 'low';
    keyPoints: string[];
  }[];
  marketImpact: string;
  sectorTrends: {
    sector: string;
    trend: string;
    affectedStocks: string[];
  }[];
}

export const geminiApi = {
  async analyzeStock(symbol: string, price: number, volume: number, historicalData?: any[], opts?: { profileId?: string }): Promise<StockAnalysis> {
    const params = new URLSearchParams({
      action: 'stock',
      symbol,
      price: price.toString(),
      volume: volume.toString()
    });
    
    if (historicalData) {
      params.append('historicalData', JSON.stringify(historicalData));
    }
    
    if (opts?.profileId) params.append('profileId', opts.profileId);
    const url = `${getApiEndpoint()}/analysis?${params}`;
    try { console.log('[geminiApi] analyzeStock GET', { url, symbol, price, volume, hasHistorical: Boolean(historicalData), profileId: opts?.profileId }); } catch {}
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`Stock analysis API error: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json() as { data: StockAnalysis };
    try { console.log('[geminiApi] analyzeStock OK', { symbol, sentiment: data?.data?.sentiment, keyPoints: data?.data?.keyPoints?.length }); } catch {}
    return data.data;
  },

  async analyzeNews(newsArticles: any[], opts?: { profileId?: string }): Promise<NewsAnalysis> {
    const params = new URLSearchParams({
      action: 'news',
      articles: JSON.stringify(newsArticles)
    });
    if (opts?.profileId) params.append('profileId', opts.profileId);
    
    const response = await fetch(`${getApiEndpoint()}/analysis?${params}`);
    
    if (!response.ok) {
      throw new Error(`News analysis API error: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json() as { data: NewsAnalysis };
    return data.data;
  },

  async generateTradingStrategy(symbol: string, marketConditions?: any): Promise<string> {
    const params = new URLSearchParams({
      action: 'strategy',
      symbol
    });
    
    if (marketConditions) {
      params.append('marketConditions', JSON.stringify(marketConditions));
    }
    
    const response = await fetch(`${getApiEndpoint()}/analysis?${params}`);
    
    if (!response.ok) {
      throw new Error(`Trading strategy API error: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json() as { data: { strategy: string; symbol: string } };
    return data.data.strategy;
  },

  async analyzeMarketTrends(marketData?: any, opts?: { profileId?: string }): Promise<string> {
    const params = new URLSearchParams({
      action: 'trends'
    });
    
    if (marketData) {
      params.append('marketData', JSON.stringify(marketData));
    }
    
    if (opts?.profileId) params.append('profileId', opts.profileId);
    const response = await fetch(`${getApiEndpoint()}/analysis?${params}`);
    
    if (!response.ok) {
      throw new Error(`Market trends API error: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json() as { data: { trends: string[]; drivers: string[]; sentiment: string; sentimentAnalysis: string } };
    
    // Return as JSON string to maintain backward compatibility
    return JSON.stringify(data.data);
  }
}; 
